import React from 'react';

import { useAppDispatch } from '@/store';
import { sessionFetchInvoiceStatus } from '@/store/slices/session';
import { QaFinalizeButton, QaFinalizeLabel } from './SpeiInstructions.styles';

/**
 * TEST-ONLY QA affordance on the SPEI instructions screen.
 *
 * Opens the commerce-gate `test_finalize_payment_url` in a new tab so QA can SIMULATE the buyer's
 * SPEI transfer settling (it marks the invoice paid without money moving). It is a settlement
 * back-door, so it only ever mounts when {@link ./data/qa-finalize.readQaFinalizeUrl} handed the
 * checkout a URL, which is itself double-gated (non-production AND the backend attached one). In
 * production the URL is always `null`, so this control never renders for a real buyer.
 *
 * Isolated into its OWN component on purpose. It reads redux (`useAppDispatch`), and the nova
 * Playwright CT harness deliberately mounts without a store Provider (importing `@/store` runs the
 * root saga and hangs every mount, see `playwright/index.tsx`). Keeping the hook here — rather
 * than in `SpeiInstructions` — means it runs only when this control is actually rendered, which
 * the existing `Spei.ct.tsx` mounts never do (they pass no QA URL), so they stay Provider-free.
 */
export function QaFinalizeControl({ url }: { url: string }): React.ReactElement {
  const dispatch = useAppDispatch();
  // Set once the back-door has actually been opened. A plain tab switch must NOT trigger a
  // re-check; only a return to the tab AFTER the QA settlement was requested does.
  const usedRef = React.useRef(false);

  // Sync with the browser's tab-visibility, an external system: when the buyer/QA comes back to
  // this tab after using the back-door, force the same one-shot invoice-status read
  // `useResumePaidSession` uses on mount, so the session catches a settlement the backgrounded
  // websocket may have missed. `force` bypasses the thunk's settled-read dedup; the sessionId
  // falls back to the store inside the thunk. Cleaned up on unmount.
  React.useEffect(() => {
    const onVisibilityChange = (): void => {
      if (document.visibilityState !== 'visible' || !usedRef.current) return;
      void dispatch(sessionFetchInvoiceStatus({ force: true }));
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [dispatch]);

  return (
    <QaFinalizeButton
      type="button"
      data-ga-id="cfg-mx-spei-qa-finalize"
      data-testid="spei-qa-finalize"
      onClick={() => {
        // SYNCHRONOUS: open the tab with no await before it, or the popup blocker eats it.
        usedRef.current = true;
        window.open(url, '_blank', 'noopener');
      }}
    >
      <QaFinalizeLabel text="Spei Debug" tag="span" />
    </QaFinalizeButton>
  );
}

export default QaFinalizeControl;
